import { useEffect, useState } from "react";
import { useAppSelector } from "hooks";
import { selectPWAState } from "redux/slices/pwa";
import { useWebStoreContext } from "contexts";
import { usePushNotificationsContext } from "contexts/PushNotificationsProvider";
import { getIsStandalonePWA } from "../helpers/getIsStandalonePWA";
import {
	PushOptInContext,
	checkPushPopupState
} from "../helpers/checkPushPopupState";
import { PushLocalState } from "interfaces/pwa";

export const usePushShouldShowPopup = (): boolean => {
	const { hasCompletedPWAInit } = useAppSelector(selectPWAState);
	const {
		isSupported,
		isSubscribed,
		permission,
		pushLocalState,
		setPushLocalState
	} = usePushNotificationsContext();
	const { nowTime } = useWebStoreContext();
	const [shouldShow, setShouldShow] = useState(false);

	useEffect(() => {
		if (typeof window === "undefined") {
			setShouldShow(false);
			return;
		}

		if (!hasCompletedPWAInit) {
			setShouldShow(false);
			return;
		}

		const isStandalone = getIsStandalonePWA();
		const nowMillis = nowTime ? nowTime.valueOf() : null;

		const context: PushOptInContext = {
			isStandalone,
			isSupported,
			isSubscribed,
			permission,
			nowMillis
		};

		// Temporary debug log to inspect push context when deciding popup visibility
		// eslint-disable-next-line no-console
		// console.log("[Push] usePushShouldShowPopup context", {
		// 	isStandalone,
		// 	isSupported,
		// 	isSubscribed,
		// 	permission,
		// 	nowMillis,
		// 	pushLocalState
		// });

		const currentState: PushLocalState = pushLocalState ?? {};
		const { nextState, shouldShow } = checkPushPopupState(
			context,
			currentState
		);

		const hasChanged =
			!pushLocalState ||
			pushLocalState.everEnabled !== nextState.everEnabled ||
			pushLocalState.disabledAt !== nextState.disabledAt ||
			pushLocalState.neverShowPopup !== nextState.neverShowPopup ||
			pushLocalState.initialPopupShown !== nextState.initialPopupShown;

		if (hasChanged) {
			setPushLocalState(nextState);
		}

		setShouldShow(shouldShow);
	}, [
		hasCompletedPWAInit,
		isSupported,
		isSubscribed,
		permission,
		nowTime,
		pushLocalState,
		setPushLocalState
	]);

	return shouldShow;
};
